import { Stack } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Flow } from '@thive/fddm-types';

import { Accordion } from '@/modules/common/components/Accordion';
import { InfoText } from '@common/components/InfoText';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { OrderProfileButton, OrderProfilePanel } from '@/modules/orderProfile';
import { useOrderProfileStore } from '@/modules/orderProfile/store/useOrderProfileStore';
import { FlowCard } from '@/modules/flowsV2/components';
import { FlowsMultiEdit } from '@/modules/flowsV2/components/FlowsMultiEdit';
import { validateFlows } from '@/modules/flowsV2/validation/flowValidationSelector';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useAccordionExpanded } from '@/modules/userPreferences/hooks';

import { ActiveFlow } from './ActiveFlow';
import { UserPreferenceName } from '@/modules/userPreferences';
import { PANEL_ACCORDION_SX } from '@/modules/common/constants/propertyPanel';

interface Props {
  /**
   * The selected ids with templates and groups replaced by their areas: their flows show.
   * Selected flows stay in it as they are.
   */
  relatedItemIds: string[];
  selectedFlows: Flow[];
}

function FlowsPanelComponent({ relatedItemIds, selectedFlows }: Props) {
  const { t } = useTranslation('interface');
  const panelRef = useRef<HTMLDivElement>(null);
  const { expanded: open, setExpanded: setOpen } = useAccordionExpanded(
    UserPreferenceName.ACCORDION_PROPERTY_PANEL_FLOWS,
  );
  const orderProfile = useOrderProfileStore((state) => state.orderProfile);
  const previousOrderProfileRef = useRef(orderProfile);
  const designFlows = useFloorplanDesignStore((state) => state.design.flows);
  const getRelatedFlows = useFloorplanDesignStore((state) => state.getRelatedFlows);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const { flows } = designFlows;

  const visibleFlows = useMemo(() => {
    // Otherwise show all flows
    if (relatedItemIds.length === 0) return flows;

    // If flows are selected, show only selected flows
    const relatedFlows = getRelatedFlows(relatedItemIds);

    return [
      ...new Map([...relatedFlows, ...selectedFlows].map((flow) => [flow.id, flow])).values(),
    ];
  }, [flows, getRelatedFlows, relatedItemIds, selectedFlows]);

  // Only flows selected: those are edited together instead of one card each
  const isMultiEdit = selectedFlows.length > 1 && selectedFlows.length === relatedItemIds.length;

  const visibleFlowIds = useMemo(() => new Set(visibleFlows.map(({ id }) => id)), [visibleFlows]);

  const errorsByFlowId = useMemo(
    () => new Map(validateFlows(flows).map((error) => [error.flowId, error])),
    [flows],
  );

  const onDelete = useCallback(
    (flow: Flow) => {
      fddmSvc.deleteFlows([flow]);
    },
    [fddmSvc],
  );

  // Show the flows when a new order profile comes in, but not on mount: that would ignore the
  // remembered state every time the layout tab opens
  useEffect(() => {
    if (previousOrderProfileRef.current === orderProfile) return;
    previousOrderProfileRef.current = orderProfile;
    setOpen(true);
  }, [orderProfile, setOpen]);

  return (
    <>
      <OrderProfilePanel anchor={panelRef} />
      <Accordion
        ref={panelRef}
        title={t('properties.flows.header', { count: visibleFlows.length })}
        afterIcon={<OrderProfileButton />}
        key='layout'
        expanded={open}
        onChange={setOpen}
        sx={PANEL_ACCORDION_SX}
      >
        <Stack>
          <Stack
            flex='1 1 auto'
            sx={{
              overflowY: 'scroll',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {/* Every flow stays mounted and only its visibility changes with the selection:
                (re)building all FlowCards on every selection change is expensive. FlowCard is
                memoized and its props don't change with the selection, so hidden cards don't
                re-render either. */}
            {isMultiEdit && (
              <FlowsMultiEdit flows={selectedFlows} errorsByFlowId={errorsByFlowId} />
            )}
            {flows.map((flow) => (
              <div key={flow.id} hidden={isMultiEdit || !visibleFlowIds.has(flow.id)}>
                <FlowCard
                  flow={flow}
                  tooltipTitle={t('properties.flows.delete.tooltip')}
                  onDelete={onDelete}
                  error={errorsByFlowId.get(flow.id)}
                />
              </div>
            ))}
            <ActiveFlow />

            {flows.length === 0 && (
              <InfoText
                header={t('properties.flows.no_flows.title')}
                subHeader={t('properties.flows.no_flows.subtitle')}
                sx={{ width: '90%', margin: '0 auto' }}
              />
            )}
          </Stack>
        </Stack>
      </Accordion>
    </>
  );
}

export const FlowsPanel = memo(FlowsPanelComponent);
